import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AuthService, DocumentPrintService, PipelineDocumentTypes, UserService } from 'tms-lib';
import { ReportNames } from '../../constants';
import { ProductLabelPiecesRequest } from '../../models';
import { StockItemService } from './stock-item.service';

/**
 * Prints the Product Label for goods just received (4Spaces, 24 Sep 2026): one label per item,
 * to the user's printer for the Product Label, with no viewer.
 *
 * Three receipt screens call this - the receipt screen, Receipt Multi Select and Production Items -
 * so the asking and the printing live here once. The API decides WHICH pieces (the Product Type's
 * "Product Label on Receipt" and the document's own auto-print); this only prints them.
 *
 * Nothing happens, and nothing fails, when no piece qualifies or the user has no printer mapped to
 * the Product Label - the document print service returns straight away, as it does for the stock
 * label.
 *
 * ⚠️ Returns the print as an Observable rather than subscribing: a screen that already prints
 * other labels must CHAIN this after them. QZ serves the whole page from one queue, and a second
 * job opening a connection while the first is printing fails (ui.md, "QZ prints are chained by
 * EMISSION").
 */
@Injectable({
  providedIn: 'root',
})
export class ProductLabelPrintService {
  private readonly stockItemService = inject(StockItemService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly documentPrintService = inject(DocumentPrintService);

  printForReceipt(request: ProductLabelPiecesRequest): Observable<unknown> {
    return forkJoin({
      stockItemIds: this.stockItemService.productLabelPieces(request),
      printer: this.userService.getUserPrinterForDocument(this.authService.userId, ReportNames.productLabel),
    }).pipe(
      switchMap(({ stockItemIds, printer }) =>
        this.documentPrintService.print({
          documentType: PipelineDocumentTypes.productLabel,
          reportIds: stockItemIds,
          printer,
          title: $localize`Product Label`,
        }),
      ),
    );
  }
}
